/**
 * Google Analytics Utilities (Firebase Analytics)
 * Handles UTM parameter tracking, campaign attribution, and GA4 event logging
 * Uses Firebase Analytics SDK for all GA4 tracking
 */

import { useEffect } from "react";
import { useRouter } from "next/router";

import { logEvent, setUserProperties } from "@/lib/firebaseConfig";

// UTM Parameters interface
export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

// Campaign data interface for storage
export interface CampaignData extends UTMParameters {
  landing_page?: string;
  referrer?: string;
  timestamp?: string;
}

const CAMPAIGN_STORAGE_KEY = "uraharu_campaign_data";
const CAMPAIGN_EXPIRY_DAYS = 30; // Campaign attribution window

/**
 * Extract UTM parameters from URL
 */
export const getUTMParameters = (url?: string): UTMParameters => {
  try {
    const searchParams = new URLSearchParams(
      url ? new URL(url).search : window.location.search
    );
    
    const utmParams: UTMParameters = {};
    
    if (searchParams.has("utm_source")) {
      utmParams.utm_source = searchParams.get("utm_source") || undefined;
    }
    if (searchParams.has("utm_medium")) {
      utmParams.utm_medium = searchParams.get("utm_medium") || undefined;
    }
    if (searchParams.has("utm_campaign")) {
      utmParams.utm_campaign = searchParams.get("utm_campaign") || undefined;
    }
    if (searchParams.has("utm_term")) {
      utmParams.utm_term = searchParams.get("utm_term") || undefined;
    }
    if (searchParams.has("utm_content")) {
      utmParams.utm_content = searchParams.get("utm_content") || undefined;
    }
    
    return utmParams;
  } catch (error) {
    console.error("Error extracting UTM parameters:", error);
    return {};
  }
};

/**
 * Check if any UTM parameters exist
 */
export const hasUTMParameters = (params: UTMParameters): boolean => {
  return Object.values(params).some((value) => value !== undefined);
};

/**
 * Save campaign data to localStorage with expiry
 */
export const saveCampaignData = (data: CampaignData): void => {
  try {
    if (typeof window === "undefined") return;
    
    const campaignData = {
      ...data,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(campaignData));
  } catch (error) {
    console.error("Error saving campaign data:", error);
  }
};

/**
 * Get saved campaign data from localStorage
 */
export const getCampaignData = (): CampaignData | null => {
  try {
    if (typeof window === "undefined") return null;
    
    const stored = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!stored) return null;
    
    const data: CampaignData = JSON.parse(stored);
    
    // Check if data has expired
    if (data.timestamp) {
      const savedDate = new Date(data.timestamp);
      const now = new Date();
      const daysDiff = Math.floor(
        (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysDiff > CAMPAIGN_EXPIRY_DAYS) {
        localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
        return null;
      }
    }
    
    return data;
  } catch (error) {
    console.error("Error getting campaign data:", error);
    return null;
  }
};

/**
 * Capture and store campaign data from current URL
 */
export const captureCampaignData = (): CampaignData | null => {
  try {
    if (typeof window === "undefined") return null;
    
    const utmParams = getUTMParameters();
    
    // Only save if we have UTM parameters
    if (hasUTMParameters(utmParams)) {
      const campaignData: CampaignData = {
        ...utmParams,
        landing_page: window.location.pathname,
        referrer: document.referrer || undefined
      };
      
      saveCampaignData(campaignData);
      return campaignData;
    }
    
    // If no UTM params in current URL, return existing saved data
    return getCampaignData();
  } catch (error) {
    console.error("Error capturing campaign data:", error);
    return null;
  }
};

/**
 * Clear campaign data from localStorage
 */
export const clearCampaignData = (): void => {
  try {
    if (typeof window === "undefined") return;
    localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing campaign data:", error);
  }
};

/**
 * Log page view to GA4 with campaign parameters
 * Uses Firebase Analytics SDK
 */
export const logPageView = (url: string): void => {
  try {
    if (typeof window === "undefined") return;
    
    const campaignData = getCampaignData();
    const pageViewData: any = {
      page_path: url,
      page_title: typeof document !== "undefined" ? document.title : ""
    };
    
    // Add campaign parameters if available
    if (campaignData) {
      if (campaignData.utm_source) {
        pageViewData.campaign_source = campaignData.utm_source;
      }
      if (campaignData.utm_medium) {
        pageViewData.campaign_medium = campaignData.utm_medium;
      }
      if (campaignData.utm_campaign) {
        pageViewData.campaign_name = campaignData.utm_campaign;
      }
      if (campaignData.utm_term) {
        pageViewData.campaign_term = campaignData.utm_term;
      }
      if (campaignData.utm_content) {
        pageViewData.campaign_content = campaignData.utm_content;
      }
    }
    
    logEvent("page_view", pageViewData);
  } catch (error) {
    console.error("Error logging page view:", error);
  }
};

/**
 * Set user properties for GA4 including campaign data
 * Uses Firebase Analytics SDK
 */
export const setGAUserProperties = (properties: {
  user_id?: string;
  sex?: string;
  age?: string;
  location?: string;
  subscription?: string;
  [key: string]: string | undefined;
}): void => {
  try {
    if (typeof window === "undefined") return;
    
    const campaignData = getCampaignData();
    const userProps: any = { ...properties };
    
    // Add campaign source as user property for attribution
    if (campaignData) {
      if (campaignData.utm_source) {
        userProps.acquisition_source = campaignData.utm_source;
      }
      if (campaignData.utm_medium) {
        userProps.acquisition_medium = campaignData.utm_medium;
      }
      if (campaignData.utm_campaign) {
        userProps.acquisition_campaign = campaignData.utm_campaign;
      }
    }
    
    setUserProperties(userProps);
  } catch (error) {
    console.error("Error setting user properties:", error);
  }
};

/**
 * Log custom event to GA4
 * Uses Firebase Analytics SDK
 */
export const logGAEvent = (
  eventName: string,
  eventParams?: { [key: string]: any }
): void => {
  try {
    if (typeof window === "undefined") return;
    
    const campaignData = getCampaignData();
    const params = { ...eventParams };
    
    // Add campaign context to events
    if (campaignData) {
      if (campaignData.utm_source) {
        params.campaign_source = campaignData.utm_source;
      }
      if (campaignData.utm_medium) {
        params.campaign_medium = campaignData.utm_medium;
      }
      if (campaignData.utm_campaign) {
        params.campaign_name = campaignData.utm_campaign;
      }
    }
    
    logEvent(eventName, params);
  } catch (error) {
    console.error("Error logging event:", error);
  }
};

/**
 * Log sign_up event with user demographics and campaign attribution
 * 
 * Automatically includes campaign data (source, medium, campaign) from localStorage
 * 
 * @example
 * ```tsx
 * // Email sign-up
 * logSignUpEvent({
 *   method: "email",
 *   user_id: user.uid,
 *   sex: "male",
 *   age: 27,
 *   location: "東京都"
 * });
 * 
 * // Twitter sign-up
 * logSignUpEvent({
 *   method: "twitter",
 *   user_id: user.uid,
 *   sex: "female",
 *   age: 25,
 *   location: "大阪府"
 * });
 * ```
 * 
 * @param params - User sign-up information
 * @param params.method - Sign-up method ("email" or "twitter")
 * @param params.user_id - User's unique identifier
 * @param params.sex - User's gender
 * @param params.age - User's age
 * @param params.location - User's location (prefecture)
 */
export const logSignUpEvent = (params: {
  method: "email" | "twitter";
  user_id: string;
  sex: string;
  age: number;
  location: string;
}): void => {
  try {
    if (typeof window === "undefined") return;

    const campaignData = getCampaignData();
    const signUpEventParams: any = {
      method: params.method,
      user_id: params.user_id,
      sex: params.sex,
      age: params.age,
      location: params.location
    };

    // Add campaign attribution if available
    if (campaignData) {
      if (campaignData.utm_source) {
        signUpEventParams.source = campaignData.utm_source;
      }
      if (campaignData.utm_medium) {
        signUpEventParams.medium = campaignData.utm_medium;
      }
      if (campaignData.utm_campaign) {
        signUpEventParams.campaign = campaignData.utm_campaign;
      }
    }

    logEvent("sign_up", signUpEventParams);
  } catch (error) {
    console.error("Error logging sign_up event:", error);
  }
};

/**
 * Custom React Hook for Analytics Integration
 * 
 * Automatically handles:
 * - UTM parameter capture on initial page load
 * - Page view tracking on route changes
 * - Campaign attribution with 30-day window
 * 
 * @example
 * ```tsx
 * // In your App component (_app.tsx)
 * import { useAnalytics } from '@/utils/analytics';
 * 
 * function App({ Component, pageProps }) {
 *   useAnalytics(); // That's it!
 *   
 *   return <Component {...pageProps} />;
 * }
 * ```
 * 
 * @param options - Optional configuration
 * @param options.enablePageTracking - Enable automatic page view tracking (default: true)
 * @param options.enableUTMCapture - Enable UTM parameter capture (default: true)
 */
export const useAnalytics = (options?: {
  enablePageTracking?: boolean;
  enableUTMCapture?: boolean;
}) => {
  const router = useRouter();
  const enablePageTracking = options?.enablePageTracking ?? true;
  const enableUTMCapture = options?.enableUTMCapture ?? true;

  useEffect(() => {
    // Capture UTM parameters on first page load
    if (enableUTMCapture) {
      captureCampaignData();
    }

    // Track page views
    if (enablePageTracking) {
      // Log initial page view
      logPageView(router.asPath);

      // Track page views on route change
      const handleRouteChange = (url: string) => {
        logPageView(url);
      };

      router.events.on("routeChangeComplete", handleRouteChange);

      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    }
  }, [router, enablePageTracking, enableUTMCapture]);
};
